import { useEffect, useId, useRef, useState } from "react";
import type { Locale } from "../api/types";

interface Props {
  locales: Locale[];
  value: string;
  onChange: (code: string) => void;
  /** "ghost" matches the cream Quiz footer; "chip" matches the dark chat topbar. */
  variant?: "ghost" | "chip";
  /** Optional label shown before the trigger (e.g., "Sprache"). */
  label?: string;
}

export function LanguagePicker({
  locales,
  value,
  onChange,
  variant = "ghost",
  label,
}: Props) {
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();

  const current = locales.find((l) => l.code === value) ?? locales[0];

  // Sort: required + full content first, then required-but-fallback, then rest
  const sorted = [...locales].sort((a, b) => {
    const rank = (l: Locale) =>
      l.has_full_content ? 0 : l.is_required ? 1 : 2;
    return rank(a) - rank(b) || a.code.localeCompare(b.code);
  });

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(sorted.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const next = sorted[focusIdx];
        if (next) {
          onChange(next.code);
          setOpen(false);
        }
      } else if (e.key === "Home") {
        e.preventDefault();
        setFocusIdx(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setFocusIdx(sorted.length - 1);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, sorted, focusIdx, onChange]);

  // Reset focus to the current item when opening
  useEffect(() => {
    if (open) {
      const i = sorted.findIndex((l) => l.code === value);
      setFocusIdx(i >= 0 ? i : 0);
    }
  }, [open, sorted, value]);

  const triggerClass = variant === "chip" ? "chip-btn lang-picker__trigger" : "ghost mono lang-picker__trigger";

  return (
    <div className={`lang-picker ${open ? "is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((o) => !o)}
      >
        {label && <span className="lang-picker__label">{label} · </span>}
        <span className="lang-picker__flag">{current?.flag_label}</span>
        <span className="lang-picker__chev" aria-hidden="true">
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open && (
        <ul
          id={listboxId}
          className="lang-picker__menu"
          role="listbox"
          aria-label={label ?? "Language"}
        >
          {sorted.map((l, i) => {
            const isCurrent = l.code === value;
            const isFocused = i === focusIdx;
            return (
              <li
                key={l.code}
                role="option"
                aria-selected={isCurrent}
                className={`lang-picker__item ${isCurrent ? "is-current" : ""} ${
                  isFocused ? "is-focused" : ""
                } ${l.has_full_content ? "" : "is-fallback"}`}
                onMouseEnter={() => setFocusIdx(i)}
                onClick={() => {
                  onChange(l.code);
                  setOpen(false);
                }}
              >
                <span className="lang-picker__item-flag mono">{l.flag_label}</span>
                <span className="lang-picker__item-name">{l.display_name}</span>
                <span className="lang-picker__item-tag mono">
                  {l.has_full_content
                    ? "✓"
                    : l.is_required
                      ? "EN"
                      : "EN"}
                </span>
              </li>
            );
          })}
          <li className="lang-picker__legend mono" aria-hidden="true">
            ✓ vollständig · EN = Englisch-Fallback
          </li>
        </ul>
      )}
    </div>
  );
}
